// The state frame (D193, boards `states-*`): one way every page inside the shell waits, fails and refuses, so the chrome
// stays real while a part of the page is not. A `Part` wraps a piece that reads with a suspense query: nothing for the
// first 300 ms, then its skeleton in the real layout (a `status` with `aria-busy`); when the read fails only that part
// errors — the catalogue's words for the code, the request id (or the code) to copy, Try again — and the rest of the page
// stays. A refused read (a role, a feature off) stays inside the shell and names who can help; offline says so with no
// code. A 401 shows nothing here: the session module is already on its way to sign-in. `RouteError` and `RouteSkeleton`
// are the router's defaults, for a loader that fails or takes long, so a page that fails as a whole still keeps the shell.
import { ApiError } from '@orca/api-client'
import { ids } from '@orca/api-client/testIds'
import { isCancelledError, QueryErrorResetBoundary, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ErrorComponentProps, useParams, useRouter } from '@tanstack/react-router'
import { Component, type ReactNode, Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { CopyField } from '../../components/ui/copy-field'
import { Icon } from '../../components/ui/icons'
import { Skeleton } from '../../components/ui/skeleton'
import { membersQuery } from '../../features/members/queries'
import { present } from '../errors/presentation'
import { membershipBySlug, meQuery } from '../org/me'

/** How long a wait shows nothing before the skeleton (D193). */
export const SKELETON_AFTER_MS = 300

/** The outline of a part while it loads: nothing for [delay] ms, then [children] or three rows. */
export function PartSkeleton({
  children,
  label,
  testId = ids.shell.partSkeleton,
  delay = SKELETON_AFTER_MS,
}: {
  children?: ReactNode
  label?: string
  testId?: string
  delay?: number
}) {
  const { t } = useTranslation()
  const [shown, setShown] = useState(delay <= 0)
  useEffect(() => {
    if (delay <= 0) return
    const timer = setTimeout(() => setShown(true), delay)
    return () => clearTimeout(timer)
  }, [delay])
  if (!shown) return null
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label ?? t('core.state.loading')}
      data-testid={testId}
      className="flex flex-col gap-(--stack-sm)"
    >
      {children ?? (
        <>
          <Skeleton className="h-15 w-full" />
          <Skeleton className="h-15 w-full" />
          <Skeleton className="h-15 w-2/3" />
        </>
      )}
    </div>
  )
}

/** A refused part (D193): it stays where it is and names who can give access — the owner, when the list shows one. */
export function NoAccessPart({ code, orgId }: { code: string; orgId?: string }) {
  const { data } = useQuery({ ...membersQuery(orgId ?? '', { role: 'owner' }), enabled: !!orgId })
  return <NoAccessCard code={code} owner={data?.items.find((m) => m.role === 'owner')?.displayName} />
}

/** The refusal's card: the lock, that this is not the person's, and who can help. */
export function NoAccessCard({ code, owner }: { code: string; owner?: string }) {
  const { t } = useTranslation()
  return (
    <Card data-testid={ids.shell.noAccessValue} data-code={code} className="flex-row items-start gap-(--inline-md)">
      <Icon name="lock" aria-hidden className="mt-0.5 size-(--icon-md) shrink-0 text-fg-muted" />
      <div className="flex flex-col gap-(--stack-xs)">
        <p className="font-bold">{t('core.state.noAccess')}</p>
        <p className="text-fg-muted">
          {owner ? t('core.state.noAccessWho', { name: owner }) : t('core.state.noAccessNobody')}
        </p>
      </div>
    </Card>
  )
}

/**
 * What a failed part shows, decided by the error table (presentation.ts). With [onBack] the page failed as a whole: it
 * says it could not load and why in its own words, and offers Back beside Try again.
 */
export function PartError({
  error,
  onRetry,
  onBack,
  orgId,
}: {
  error: unknown
  onRetry: () => void
  onBack?: () => void
  orgId?: string
}) {
  const { t, i18n } = useTranslation()
  const reaction = present(error)
  if (reaction.kind === 'endSession') return null
  const api = error instanceof ApiError ? error : undefined
  const code = reaction.kind === 'offline' ? 'offline' : (api?.effectiveCode ?? 'unexpected')
  if (reaction.kind === 'toast' && reaction.refreshAccess) return <NoAccessPart code={code} orgId={orgId} />
  const whole = onBack !== undefined && reaction.kind !== 'offline'
  const title =
    reaction.kind === 'offline'
      ? t('core.error.offline.title')
      : whole
        ? t('core.state.failed')
        : api && i18n.exists(api.messageKey)
          ? t(api.messageKey, reaction.kind === 'toast' ? reaction.params : undefined)
          : api && i18n.exists(`error.${code}`)
            ? t(`error.${code}`)
            : t('core.error.unexpected')
  const copyable = reaction.kind === 'offline' ? undefined : (api?.requestId ?? code)
  return (
    <Card
      role="alert"
      data-testid={ids.shell.partErrorValue}
      data-code={code}
      data-scope={whole ? 'page' : 'part'}
      className="flex-row flex-wrap items-start gap-(--inline-md)"
    >
      <Icon
        name={reaction.kind === 'offline' ? 'offline' : 'alert'}
        aria-hidden
        className="mt-0.5 size-(--icon-md) shrink-0 text-fg-muted"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-(--stack-sm)">
        <p className="font-bold">{title}</p>
        {reaction.kind === 'offline' && <p className="text-fg-muted">{t('core.error.offline')}</p>}
        {whole && <p className="text-fg-muted">{t('core.state.failedHint')}</p>}
        {copyable && (
          <CopyField
            label={api?.requestId ? t('core.state.requestId') : t('core.state.code')}
            value={copyable}
            icon="info"
            testId={ids.shell.partCodeField}
          />
        )}
        <div className="flex flex-wrap gap-(--inline-sm)">
          <Button variant="secondary" size="sm" onClick={onRetry} data-testid={ids.shell.partRetryButton}>
            {t('core.action.retry')}
          </Button>
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack} data-testid={ids.shell.partBackButton}>
              {t('core.state.back')}
            </Button>
          )}
        </div>
      </div>
    </Card>
  )
}

type BoundaryProps = {
  onReset: () => void
  fallback: (error: unknown, retry: () => void, failedAt: number) => ReactNode
  children: ReactNode
}

/**
 * React has no error boundary as a function; this one holds the error until Try again. A read cancelled under way
 * (a refetch that replaced it, the session ending) is no failure: the part simply reads again.
 */
class PartBoundary extends Component<BoundaryProps, { error: unknown; failedAt: number }> {
  override state: { error: unknown; failedAt: number } = { error: undefined, failedAt: 0 }
  static getDerivedStateFromError(error: unknown) {
    return { error, failedAt: Date.now() }
  }
  override componentDidCatch(error: unknown) {
    if (isCancelledError(error)) this.retry()
  }
  retry = () => {
    this.props.onReset()
    this.setState({ error: undefined })
  }
  override render() {
    const { error } = this.state
    if (error === undefined) return this.props.children
    return isCancelledError(error) ? null : this.props.fallback(error, this.retry, this.state.failedAt)
  }
}

/**
 * A quiet part's failure: nothing shown, and the part comes back once a read succeeds after it failed (its main part's
 * Try again) — also one that landed before React showed this fallback, which it may hold back a moment.
 */
function RecoverWhenLoaded({ onLoaded, since }: { onLoaded: () => void; since: number }) {
  const queryClient = useQueryClient()
  useEffect(() => {
    const cache = queryClient.getQueryCache()
    if (cache.getAll().some((query) => query.state.dataUpdatedAt >= since)) {
      onLoaded()
      return
    }
    return cache.subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'success') onLoaded()
    })
  }, [queryClient, onLoaded, since])
  return null
}

/** One part of a page in the state frame: its skeleton while it loads, its own error when it fails. */
export function Part({
  children,
  skeleton,
  label,
  testId,
  orgId,
  quiet,
}: {
  children: ReactNode
  /** The part's own outline (rows, a card); three rows when left out. */
  skeleton?: ReactNode
  /** What the wait is for, spoken: "Loading tasks". */
  label?: string
  /** The skeleton's id: the screen's own `…Skeleton` where it has one. */
  testId?: string
  /** The organization, so a refusal can name its owner. */
  orgId?: string
  /**
   * A part drawn from what another part shows (a banner over the list): no skeleton, and when it fails nothing — the
   * main part says so — until that part loads again.
   */
  quiet?: boolean
}) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <PartBoundary
          onReset={reset}
          fallback={(error, retry, failedAt) =>
            quiet ? (
              <RecoverWhenLoaded onLoaded={retry} since={failedAt} />
            ) : (
              <PartError error={error} onRetry={retry} orgId={orgId} />
            )
          }
        >
          <Suspense
            fallback={
              quiet ? null : (
                <PartSkeleton label={label} testId={testId}>
                  {skeleton}
                </PartSkeleton>
              )
            }
          >
            {children}
          </Suspense>
        </PartBoundary>
      )}
    </QueryErrorResetBoundary>
  )
}

/** The router's error frame: a loader or a screen that failed as a whole, still inside the shell. */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  const { orgSlug } = useParams({ strict: false })
  const { data: me } = useQuery({ ...meQuery(), enabled: false })
  const orgId = orgSlug ? membershipBySlug(me ?? undefined, orgSlug)?.orgId : undefined
  const retry = () => {
    reset()
    void router.invalidate()
  }
  const cancelled = isCancelledError(error)
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per cancelled error, as PartBoundary does
  useEffect(() => {
    if (cancelled) retry()
  }, [cancelled])
  return cancelled ? null : (
    <PartError error={error} orgId={orgId} onRetry={retry} onBack={() => router.history.back()} />
  )
}

/** The router's wait (its `defaultPendingMs` is the 300 ms): the page's outline under the real chrome. */
export function RouteSkeleton() {
  return <PartSkeleton delay={0} />
}
